import type { ReactNode } from 'react';
import type { RowDataAttributes } from './DetailList';
import styles from './Card.module.css';

/**
 * 🔒 The card (piece 4; T3223): the one function a page's bordered section goes
 * through, its title at the top and the page's own content beneath. Fourteen page
 * stylesheets each carried a copy of the card and its title before, and a token
 * change to the card was fourteen edits; `cards.test.ts` holds that list and it
 * only shrinks.
 *
 * 🔨 Three densities and no fourth. `standard` is the settings, accounting and
 * reports card, its title the uppercase section label. `plain` keeps that frame
 * with a bold title in body type, which is the banking card. `roomy` pads the
 * sides wider, tightens the gap and draws the same bold body title, which is the
 * knowledge card.
 *
 * 🔨 It is neither the Tile nor the detail list. A record page's summary is a
 * Tile on its canvas, and the rows inside a card are the detail list's.
 */
export type CardDensity = 'standard' | 'plain' | 'roomy';

/** The element the card is drawn as, so a section, an article or a list item stays one. */
export type CardElement = 'div' | 'section' | 'article' | 'li';

export interface CardProps extends RowDataAttributes {
  /** The card's heading. A card without one draws its content alone. */
  title?: ReactNode;
  /** The heading's level in the page's outline; a third-level heading where none is named. */
  level?: 2 | 3 | 4;
  /**
   * Controls at the title's right, on the title's own line: an Add, an Edit, a
   * count. 🔨 Where none is passed the heading stands alone, with no row around it.
   */
  actions?: ReactNode;
  /** The look; `standard` where none is named. */
  density?: CardDensity;
  /** The element drawn; a `div` where none is named. */
  as?: CardElement;
  /** An anchor a link elsewhere on the page or in a stepper points at. */
  id?: string;
  children?: ReactNode;
}

export const cardDocumentedStates = ['standard', 'plain', 'roomy', 'actions'] as const;

const FRAME: Readonly<Record<CardDensity, string>> = { standard: styles.standard!, plain: styles.standard!, roomy: styles.roomy! };
const TITLE: Readonly<Record<CardDensity, string>> = { standard: styles.titleLabel!, plain: styles.titleBold!, roomy: styles.titleBold! };

export function Card({ title, level = 3, actions, density = 'standard', as: Element = 'div', id, children, ...data }: CardProps) {
  const Heading = `h${level}` as const;
  const heading = title === undefined ? null : <Heading className={`${styles.title} ${TITLE[density]}`}>{title}</Heading>;
  return (
    <Element className={`${styles.card} ${FRAME[density]}`} data-card={density} {...(id === undefined ? {} : { id })} {...data}>
      {actions === undefined ? heading : (
        <div className={styles.head}>
          {heading}
          <div className={styles.actions}>{actions}</div>
        </div>
      )}
      {children}
    </Element>
  );
}
